import React, { createContext, useState, useEffect, useContext } from 'react';
import API from '../utils/api';

const AdminContext = createContext();

export const AdminProvider = ({ children }) => {
    const [admin, setAdmin] = useState(null);
    const [loading, setLoading] = useState(true);

    // Mobile/tablet — the sidebar becomes a floating "Navigation" panel,
    // opened/closed from the topbar. Ephemeral, not persisted.
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const toggleMobileNav = () => setMobileNavOpen(v => !v);
    const closeMobileNav = () => setMobileNavOpen(false);

    // Desktop — expanded (labels + groups) vs collapsed (icon-only rail).
    // A lasting preference, so it's persisted and reflected on <body> as a
    // class so AdminLayout.css can resize the sidebar and content offset
    // together without every page needing to read this state itself.
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('adminSidebarCollapsed') === 'true');
    const toggleCollapsed = () => setSidebarCollapsed(v => {
        localStorage.setItem('adminSidebarCollapsed', String(!v));
        return !v;
    });
    useEffect(() => {
        document.body.classList.toggle('sidebar-collapsed', sidebarCollapsed);
    }, [sidebarCollapsed]);

    // One topbar button serves both roles depending on viewport.
    const toggleSidebar = () => {
        if (window.innerWidth <= 1024) toggleMobileNav();
        else toggleCollapsed();
    };

    useEffect(() => {
        const token = localStorage.getItem('adminToken');
        if (token) {
            API.get('/auth/me')
                .then(res => {
                    if (res.data.user.role === 'admin') {
                        setAdmin(res.data.user);
                    } else {
                        localStorage.removeItem('adminToken');
                    }
                })
                .catch(() => localStorage.removeItem('adminToken'))
                .finally(() => setLoading(false));
        } else {
            setLoading(false);
        }
    }, []);

    const login = async (email, password) => {
        const res = await API.post('/auth/admin-login', { email, password });
        if (res.data.user.role !== 'admin') {
            throw new Error('Access denied! Admin only.');
        }
        localStorage.setItem('adminToken', res.data.token);
        setAdmin(res.data.user);
        return res.data;
    };

    const logout = () => {
        localStorage.removeItem('adminToken');
        setAdmin(null);
    };

    return (
        <AdminContext.Provider value={{
            admin, setAdmin, loading, login, logout,
            mobileNavOpen, toggleMobileNav, closeMobileNav,
            sidebarCollapsed, toggleCollapsed, toggleSidebar,
        }}>
            {children}
        </AdminContext.Provider>
    );
};

export const useAdmin = () => useContext(AdminContext);
export default AdminContext;